import { FreeGift, Milestone } from './types';
import { roundTo2 } from '../../components/helpers/float';
import { logger } from '../../../common/logger';

export const getQtyOfVariantInCart = ({ sp_variant_id, mrCart }) => {
  let qty = 0;
  for (let i = 0; i < mrCart.length; i += 1) {
    const { quantity, sp_variant_id: id } = mrCart[i];
    if (id === sp_variant_id) {
      qty += quantity;
    }
  }
  return qty;
};

export const getVariantPriceMap = ({
  mrCart,
  isUseDiscountedPriceFromCart,
}) => {
  const priceMap = {};
  for (let i = 0; i < mrCart.length; i += 1) {
    const { price, discounted_price, sp_variant_id } = mrCart[i];
    priceMap[sp_variant_id] = isUseDiscountedPriceFromCart
      ? discounted_price
      : price;
  }
  return priceMap;
};

export const calculateCartAmount = (
  mrCart: {
    sp_variant_id: number;
    sp_product_id?: number;
    price: number;
    quantity: number;
    selling_plan_allocation?: {
      selling_plan: {
        id: number;
      };
    };
    discounted_price?: number;
  }[],
  cvbEligibleProducts: {
    isValid: boolean;
  }[],
  milestones: {
    rewards: {
      rewardType: string;
      freeGiftSpVariantID: number;
    }[];
  }[],
  reservedItems: {
    [key: string]: number;
  },
  isCartQuantityRestricted: boolean,
  isCVBAmountUsable: boolean = false,
  cvbCartValue?: number,
  shopURL?: string,
  isUseDiscountedPriceFromCart?: boolean,
) => {
  if (isCVBAmountUsable && shopURL !== 'wildbrands.myshopify.com') {
    return cvbCartValue;
  }
  const variantsUsedForCVBTotal = {};

  let actualAmount = mrCart.reduce((total, item, idx) => {
    const { sp_variant_id, price, sp_product_id, discounted_price } = item;

    // ignore for raevoluz
    if ([7992654692620, 7748529586411].includes(sp_product_id)) {
      return total;
    }

    if (
      [
        7048271200462,
        7048270610638,
        7048270774478,
        7048271003854,
        7048272773326,
        7048273625294,
        6300334882991,
        8100478648584,
      ].includes(sp_product_id)
    ) {
      // taynie.de // taynie.de
      return total;
    }

    // Merchant Custom: ignore subscription products
    if (
      [
        'xyzskincare.myshopify.com',
        'skincaresite.myshopify.com',
        'plentifulskin.myshopify.com',
      ].includes(shopURL)
    ) {
      const { selling_plan_allocation } = item;
      if (selling_plan_allocation) {
        const { selling_plan } = selling_plan_allocation;
        if (selling_plan) {
          const { id: selling_plan_id } = selling_plan;
          if (selling_plan_id > 0) {
            return total;
          }
        }
      }
    }

    if ([38297809223855].includes(sp_variant_id)) {
      return total;
    }

    // cvbEligibleProducts is an array of cart line items that are eligible for CVB
    // if any product is not eligible then we don't consider it in the calculation of total amount
    if (!cvbEligibleProducts || cvbEligibleProducts.length === 0) return total;
    if (!cvbEligibleProducts[idx].isValid) return total;

    const priceToUse = isUseDiscountedPriceFromCart ? discounted_price : price; // track the items used to calculate cvb total
    variantsUsedForCVBTotal[sp_variant_id] = true;
    return total + priceToUse * item.quantity;
  }, 0);

  // Define a self-invoking function to calculate reserved items based on their variants.
  // currently reserved items are stored as an object with the cart index as the key and the quantity as the value.
  const reservedItemsBasedOnVariants = (() => {
    // Initialize an empty object to hold the count of reserved items by variant ID.
    const reservedItemsBasedOnVariants = {};

    // Iterate over each entry in the reservedItems object.
    Object.entries(reservedItems).forEach(([cartIndex, quantity]) => {
      // Retrieve the cart item using the cartIndex to get the variant information.
      const cartVariant = mrCart[cartIndex];
      // Extract the variant ID from the cart item.
      const variantId = cartVariant.sp_variant_id;

      // Check if the variant ID is not already in the reservedItemsBasedOnVariants object.
      if (reservedItemsBasedOnVariants[variantId] === undefined)
        // If not present, initialize the count with the current quantity.
        reservedItemsBasedOnVariants[variantId] = quantity;
      // If already present, increment the count by the current quantity.
      else reservedItemsBasedOnVariants[variantId] += quantity;
    });

    // Return the populated object containing the count of reserved items by variant ID.
    return reservedItemsBasedOnVariants;
  })();

  const cartQuantityMap = {};

  // This function is expected to create a mapping of variant IDs to their prices.
  // The mrCart parameter is passed to include all items currently in the cart.
  // The isUseDiscountedPriceFromCart flag is set to false indicating that
  // the original price of the variants should be used instead of any discounted price.
  const cartVariantPriceMap = getVariantPriceMap({
    mrCart,
    isUseDiscountedPriceFromCart: false,
  });

  for (let i = 0; i < milestones.length; i += 1) {
    const milestone = milestones[i];
    const { rewards } = milestone;
    const freeGiftRewards = getFreeGiftRewards(rewards);

    // we subtract the amount of free gifts from the total amount
    let amountToSubtract = 0;

    if (freeGiftRewards.length > 0) {
      amountToSubtract = freeGiftRewards.reduce((acc, curr) => {
        const { freeGiftSpVariantID } = curr;
        // if amount of fg is not used in cvb total then no need to subtract
        if (!variantsUsedForCVBTotal[freeGiftSpVariantID]) {
          return acc;
        }

        const cartQuantity = getQtyOfVariantInCart({
          sp_variant_id: freeGiftSpVariantID,
          mrCart,
        });

        if (cartQuantityMap[freeGiftSpVariantID] === undefined)
          cartQuantityMap[freeGiftSpVariantID] = cartQuantity;

        // currently we consider reserved quantity only in case of auto remove free gifts is disabled
        // auto remove free gifts is disabled that means BOGO is enabled and we can consider the price of free gifts in calculation of total amount
        //  else we always subtract the price of free gifts from the total amount
        const consideredReservedFgQuantity = !isCartQuantityRestricted
          ? reservedItemsBasedOnVariants[freeGiftSpVariantID] || 0
          : 0;

        // total quantity in cart subtracting reserved quantity of fg will give the total number of discount fgs
        //  all the reserved fgs are using calculation of total amount
        const fgQuantity =
          cartQuantityMap[freeGiftSpVariantID] - consideredReservedFgQuantity;

        // if fg quantity is less than or equal that means it's a discounted fg not used in calculation of total amount
        if (fgQuantity <= 0) return acc;

        // subtract the used reserved  quantity from the reserved quantity and cart quantity map
        reservedItemsBasedOnVariants[
          freeGiftSpVariantID
        ] -= consideredReservedFgQuantity;
        cartQuantityMap[freeGiftSpVariantID] -= fgQuantity;

        // add the total amount of fg so that it can be subtracted
        return acc + fgQuantity * cartVariantPriceMap[freeGiftSpVariantID];
      }, amountToSubtract);
    }
    // here we subtract the cart amount by the amount of free gifts discounted not used in milestone based on above conditions
    actualAmount -= amountToSubtract;
  }

  return actualAmount;
};

const getFreeGiftRewards = rewards => rewards.filter(r => r.rewardType === 'freegift')
  .reduce((acc, curr) => {
    if(curr.freeGiftAvailingType === 'auto_add') {
      return [...acc, curr];
    }
    // extracting all the variants from products
    // because all the variants are offered rewards
    return [...acc, ...curr.rewardProducts.reduce((fgAcc, fg) => {
      // taking all the variants
      return [...fgAcc, ...fg.variants.map(v => ({ freeGiftSpVariantID: v.id }))];
    }, [])];
  }, []);

export const calculateCartQuantity = (
  mrCart: {
    sp_variant_id: number;
    sp_product_id?: number;
    price: number;
    quantity: number;
    selling_plan_allocation?: {
      selling_plan: {
        id: number;
      };
    };
    discounted_price?: number;
  }[],
  cvbEligibleProducts: {
    isValid: boolean;
  }[],
  milestones: {
    rewards: {
      rewardType: string;
      freeGiftSpVariantID: number;
    }[];
  }[],
  reservedItems: {
    [key: string]: number;
  },
  isCartQuantityRestricted: boolean,
  isCVBQuantityUsable: boolean = false,
  cvbCartQuantityValue?: number,
  shopURL?: string,
) => {
  if (isCVBQuantityUsable) {
    return cvbCartQuantityValue;
  }
  const variantsUsedForCVBTotal = {};

  let actualAmount = mrCart.reduce((totalQuantity, item, idx) => {
    const { sp_product_id, sp_variant_id, quantity } = item;

    // ignore for raevoluz
    if ([7992654692620, 7748529586411].includes(sp_product_id)) {
      return totalQuantity;
    }

    // hobieeyewear.myshopify.com
    if (
      [
        7048271200462,
        7048270610638,
        7048270774478,
        7048271003854,
        7048272773326,
        7048273625294,
        6300334882991,
        8100478648584,
      ].includes(sp_product_id)
    ) {
      // taynie.de // taynie.de
      return totalQuantity;
    }

    // Merchant Custom: ignore subscription products
    if (
      [
        'xyzskincare.myshopify.com',
        'skincaresite.myshopify.com',
        'plentifulskin.myshopify.com',
      ].includes(shopURL)
    ) {
      const { selling_plan_allocation } = item;
      if (selling_plan_allocation) {
        const { selling_plan } = selling_plan_allocation;
        if (selling_plan) {
          const { id: selling_plan_id } = selling_plan;
          if (selling_plan_id > 0) {
            return totalQuantity;
          }
        }
      }
    }

    // taynie.de
    if ([38297809223855].includes(sp_variant_id)) {
      return totalQuantity;
    }

    if (!cvbEligibleProducts || cvbEligibleProducts.length === 0)
      return totalQuantity;
    if (!cvbEligibleProducts[idx].isValid) return totalQuantity;

    // track the items used to calculate cvb total
    variantsUsedForCVBTotal[sp_variant_id] = true;
    return totalQuantity + quantity;
  }, 0);

  const reservedItemsBasedOnVariants = (() => {
    const reservedItemsBasedOnVariants = {};

    Object.entries(reservedItems).forEach(([cartIndex, quantity]) => {
      const cartVariant = mrCart[cartIndex];
      const variantId = cartVariant.sp_variant_id;
      if (reservedItemsBasedOnVariants[variantId] === undefined)
        reservedItemsBasedOnVariants[variantId] = quantity;
      else reservedItemsBasedOnVariants[variantId] += quantity;
    });
    return reservedItemsBasedOnVariants;
  })();

  const cartQuantityMap = {};

  for (let i = 0; i < milestones.length; i += 1) {
    const milestone = milestones[i];
    const { rewards } = milestone;
    const freeGiftRewards = getFreeGiftRewards(rewards);
    let quantityToSubtract = 0;

    if (freeGiftRewards.length > 0) {
      quantityToSubtract = freeGiftRewards.reduce((acc, curr) => {
        const { freeGiftSpVariantID } = curr;
        // if amount of fg is not used in cvb total then no need to subtract
        if (!variantsUsedForCVBTotal[freeGiftSpVariantID]) {
          return acc;
        }

        const cartQuantity = getQtyOfVariantInCart({
          sp_variant_id: freeGiftSpVariantID,
          mrCart,
        });

        if (cartQuantityMap[freeGiftSpVariantID] === undefined)
          cartQuantityMap[freeGiftSpVariantID] = cartQuantity;

        const consideredReservedFgQuantity = !isCartQuantityRestricted
          ? reservedItemsBasedOnVariants[freeGiftSpVariantID] || 0
          : 0;

        const fgQuantity =
          cartQuantityMap[freeGiftSpVariantID] - consideredReservedFgQuantity;

        if (fgQuantity <= 0) return acc;

        reservedItemsBasedOnVariants[
          freeGiftSpVariantID
        ] -= consideredReservedFgQuantity;
        cartQuantityMap[freeGiftSpVariantID] -= fgQuantity;

        // add the total amount of fg so that it can be subtracted
        return acc + fgQuantity;
      }, quantityToSubtract);
    }
    actualAmount -= quantityToSubtract;
  }

  return actualAmount;
};

export const getFreeGifts = ({
  cartValue,
  cartQuantity,
  isCVBAddRewardsOfAllMilestones,
  milestones,
  isPriceValueType,
  isQuantityValueType,
}) => {
  const eligibleFreeGifts = [];
  const fgMilestoneMap = {};

  let milestonesToUse = [...milestones].map((milestone, idx) => ({
    ...milestone,
    idx,
  }));
  let lastMilestoneIDX = -1;
  if (!isCVBAddRewardsOfAllMilestones) {
    for (let i = 0; i < milestones.length; i += 1) {
      logger.log(
        milestones[i].minValue,
        roundTo2(isPriceValueType ? cartValue : cartQuantity),
      );

      if (
        milestones[i].minValue <=
        roundTo2(isPriceValueType ? cartValue : cartQuantity)
      ) {
        lastMilestoneIDX = i;
      }
    }
    if (lastMilestoneIDX === -1) {
      return { eligibleFreeGifts, fgMilestoneMap };
    }
    milestonesToUse = [{...milestones[lastMilestoneIDX], idx: lastMilestoneIDX}];
  }

  for (let i = 0; i < milestonesToUse.length; i += 1) {
    const milestone = milestonesToUse[i];
    const { minValue, rewards } = milestone;
    const freeGiftRewards = rewards.filter(r => r.rewardType === 'freegift').map(r => {
      const updatedReward = {
        ...r,
        milestoneIdx: !isCVBAddRewardsOfAllMilestones? lastMilestoneIDX : i,
      };
      return updatedReward;
    });

    // min value is milestone value and cart value is total cart value
    if (isPriceValueType && minValue <= roundTo2(cartValue)) {
      eligibleFreeGifts.push(...freeGiftRewards);
      fgMilestoneMap[milestone.idx] = freeGiftRewards;
    }
    if (isQuantityValueType && minValue <= cartQuantity) {
      eligibleFreeGifts.push(...freeGiftRewards);
      fgMilestoneMap[milestone.idx] = freeGiftRewards;
    }
  }

  return { eligibleFreeGifts: eligibleFreeGifts.filter(r => r.freeGiftAvailingType === 'auto_add'), fgMilestoneMap };
};

export const getAllFreeGifts = milestones => {
  const allFreeGifts = [];
  const allFgMilestoneMap = {};

  for (let i = 0; i < milestones.length; i += 1) {
    for (let j = 0; j < milestones[i].rewards.length; j += 1) {
      if (milestones[i].rewards[j].rewardType === 'freegift') {
        allFreeGifts.push(milestones[i].rewards[j]);
        if (allFgMilestoneMap[i] === undefined) {
          allFgMilestoneMap[i] = [];
        }
        allFgMilestoneMap[i].push(milestones[i].rewards[j]);
      }
    }
  }
  return { allFreeGifts: allFreeGifts.filter(fg => fg.freeGiftAvailingType === 'auto_add'), allFgMilestoneMap };
};

type MilestoneBasedFgData = {
  milestoneIndex: string;
  offer: {
    allFreeGifts: FreeGift[];
    offerLevelDiscountQuantity: number;
    discountQuantity: number;
    reservedItemQuantity: {
      [key: string]: number;
    };
    offered: FreeGift[];
  }
  isEligible: boolean;
}[];

/**
 * This function generates data for free gifts based on milestones.
 * It maps through the milestone indexes and constructs an object containing
 * information about the free gifts, their availability, and related theme texts.
 *
 * @param {Milestone[]} milestones - An array of milestone objects.
 * @param {Object[]} fgMilestoneMap - An array of objects mapping milestone indexes to free gifts.
 * @param {Object[]} allFreeGiftsMilestoneMap - An array of objects mapping milestone indexes to all available free gifts.
 * @param {string} freeGiftAvailingType - The type of free gift availing (e.g., 'manual_selection').
 *
 * @returns {MilestoneBasedFgData[]} - An array of objects containing free gift data based on milestones.
 */
export const getFgDataBasedOnMilestone = (
  milestones: Milestone[],
  fgMilestoneMap: {
    [key: string]: FreeGift[];
  }[],
  allFreeGiftsMilestoneMap: {
    [key: string]: FreeGift[];
  }[],
  freeGiftAvailingType: string,
  isCartQuantityRestricted: boolean,
): MilestoneBasedFgData => {
  // Get the indexes of all milestones.
  const milestoneIndexes = Object.keys(allFreeGiftsMilestoneMap);

  // Map through each milestone index to construct the free gift data.
  return milestoneIndexes.map(idx => {
    const freeGiftData = {
      milestoneIndex: idx,
      offer: {
        // Filter all free gifts based on the availing type and get the reward products.
        allFreeGifts: allFreeGiftsMilestoneMap[idx].filter(
          fg => fg.freeGiftAvailingType === freeGiftAvailingType,
        )?.[0]?.rewardProducts || [],
        // Filter offered free gifts based on the availing type and get the reward products.
        offered: fgMilestoneMap[idx]?.filter(fg => fg.freeGiftAvailingType === freeGiftAvailingType)?.[0]?.rewardProducts || [],
        // Get the discount quantity at the milestone level.
        offerLevelDiscountQuantity: milestones[idx].milestoneLevelFreegiftDiscountQuantity,
        // Get the discount quantity per product.
        discountQuantity: milestones[idx].freegiftPerProductQuantity,
        // Get the reserved item quantity.
        reservedItemQuantity: milestones[idx].reservedItemQuantity,
        freegiftItemQuantity: milestones[idx].freegiftItemQuantity,
        // Set the type of offer to 'freegift'.
        type: 'freegift',
        // Set the free gift availing type.
        freeGiftAvailingType: 'manual_selection',
        // Set the auto remove free gifts flag.
        isCartQuantityRestricted: isCartQuantityRestricted,
      },
      theme: {
        // Get the header text for the free gift offer.
        offer_header_text: milestones[idx].milestoneFreegiftHeaderText,
        // Get the subtitle text for the free gift offer.
        offer_subtitle_text: milestones[idx].milestoneFreegiftSubtitleText,
      },
      // Determine if the milestone is eligible for the free gift offer.
      isEligible: !!fgMilestoneMap[idx],
    };
    return freeGiftData;
  }).filter(({ offer }) => offer.allFreeGifts.length > 0); // Filter out milestones with no available free gifts.
};